
  // ── Singleton overlay (shared by all nav-row instances on the page) ──────
  // One overlay element is created lazily and reused across instances.
  const NavRowOverlay = (() => {
    let _backdrop = null;
    let _panel    = null;
    let _titleEl  = null;
    let _bodyEl   = null;
    let _currentUrl = null;
    let _currentRow = null; // the nav-row instance that opened the overlay

    function _ensure() {
      if (_backdrop) return;

      // ── Backdrop ──
      _backdrop = document.createElement("div");
      _backdrop.id = "nav-row-backdrop";
      Object.assign(_backdrop.style, {
        position: "fixed", inset: "0",
        background: "rgba(0,0,0,0)",
        zIndex: "9998",
        pointerEvents: "none",
        transition: "background 0.3s ease",
      });
      _backdrop.addEventListener("click", close);

      // ── Panel ──
      _panel = document.createElement("div");
      _panel.id = "nav-row-panel";
      _panel.setAttribute("role", "dialog");
      _panel.setAttribute("aria-modal", "true");
      Object.assign(_panel.style, {
        position: "fixed",
        top: "0", right: "0", bottom: "0",
        /*width: "min(100%, 520px)",*/
        width: "100%",
        background: "var(--color-background-primary, #fff)",
        borderLeft: "0.5px solid var(--color-border-tertiary, #e0dfd7)",
        zIndex: "9999",
        display: "flex",
        flexDirection: "column",
        transform: "translateX(100%)",
        transition: "transform 0.32s cubic-bezier(0.4,0,0.2,1)",
        overflow: "hidden",
        /*fontFamily: "system-ui, sans-serif",*/
      });

      // ── Header ──
      const header = document.createElement("div");
      Object.assign(header.style, {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "16px 16px 14px",
        borderBottom: "0.5px solid var(--color-border-tertiary, #e0dfd7)",
        flexShrink: "0",
      });

      const backBtn = document.createElement("button");
      backBtn.setAttribute("aria-label", "Back to profile");
      backBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M10 4L6 8l4 4" stroke="currentColor" stroke-width="1.4"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
      Object.assign(backBtn.style, {
        display: "flex", alignItems: "center", justifyContent: "center",
        width: "32px", height: "32px",
        borderRadius: "8px",
        border: "0.5px solid var(--color-border-tertiary, #e0dfd7)",
        background: "transparent",
        cursor: "pointer",
        color: "var(--color-text-secondary, #888)",
        transition: "background 0.12s",
        flexShrink: "0",
      });
      backBtn.addEventListener("mouseenter", () => {
        backBtn.style.background = "var(--color-background-secondary, #f5f5f3)";
      });
      backBtn.addEventListener("mouseleave", () => {
        backBtn.style.background = "transparent";
      });
      backBtn.addEventListener("click", close);

      _titleEl = document.createElement("span");
      Object.assign(_titleEl.style, {
        fontSize: "15px",
        fontWeight: "500",
        color: "var(--color-text-primary, #1a1a1a)",
        flex: "1",
      });

      header.appendChild(backBtn);
      header.appendChild(_titleEl);

      // ── Body ──
      _bodyEl = document.createElement("div");
      Object.assign(_bodyEl.style, {
        flex: "1",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      });

      _panel.appendChild(header);
      _panel.appendChild(_bodyEl);
      document.body.appendChild(_backdrop);
      document.body.appendChild(_panel);

      // Keyboard: Escape closes
      document.addEventListener("keydown", e => {
        if (e.key === "Escape" && _panel.style.transform === "translateX(0%)") close();
      });
    }

    function open(rowInstance, opts) {
      _ensure();
      _currentRow = rowInstance;
      _currentUrl = opts.url;
      _titleEl.textContent = opts.title || opts.label || "";
      _panel.setAttribute("aria-label", opts.title || opts.label || "");

      // Show loading state
      _bodyEl.innerHTML = "";
      const loading = document.createElement("div");
      Object.assign(loading.style, {
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: "12px", height: "200px",
        color: "var(--color-text-tertiary, #aaa)",
        fontSize: "13px",
      });
      loading.innerHTML = `
        <div style="
          width:20px;height:20px;
          border:2px solid var(--color-border-tertiary,#e0dfd7);
          border-top-color:var(--color-text-secondary,#888);
          border-radius:50%;
          animation:navrow-spin 0.7s linear infinite">
        </div>
        Loading…`;
      _bodyEl.appendChild(loading);

      // Inject keyframe if not yet present
      if (!document.getElementById("navrow-style")) {
        const s = document.createElement("style");
        s.id = "navrow-style";
        s.textContent = "@keyframes navrow-spin{to{transform:rotate(360deg)}}";
        document.head.appendChild(s);
      }

      // Animate in
      _backdrop.style.pointerEvents = "all";
      _backdrop.style.background     = "rgba(0,0,0,0.35)";
      _panel.style.transform          = "translateX(0%)";
      document.body.style.overflow    = "hidden";

      rowInstance.dispatchEvent(new CustomEvent("nav-row-open", {
        detail: { label: opts.label, url: opts.url, title: opts.title },
        bubbles: true, composed: true,
      }));

      _fetchContent(opts.url, opts.label);
    }

    async function _fetchContent(url, label) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("HTTP " + res.status);
        const html = await res.text();
        if (url !== _currentUrl) return; // stale

        _bodyEl.innerHTML = "";
        const content = document.createElement("div");
        Object.assign(content.style, {
          padding: "5px",
          fontSize: "10px",
          lineHeight: "1.7",
          color: "var(--color-text-primary, #1a1a1a)",
        });
        content.innerHTML = html;
        _bodyEl.appendChild(content);

      } catch (err) {
        if (url !== _currentUrl) return;

        _bodyEl.innerHTML = "";
        const errEl = document.createElement("div");
        Object.assign(errEl.style, {
          padding: "2rem 1.5rem",
          textAlign: "center",
          fontSize: "13px",
          color: "var(--color-text-danger, #c0392b)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
        });
        errEl.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" color="var(--color-text-danger,#c0392b)">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 8v5M12 15.5v.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Could not load content.`;

        const retryBtn = document.createElement("button");
        retryBtn.textContent = "Retry";
        Object.assign(retryBtn.style, {
          fontSize: "13px",
          padding: "6px 14px",
          borderRadius: "8px",
          border: "0.5px solid var(--color-border-tertiary,#e0dfd7)",
          background: "var(--color-background-primary,#fff)",
          cursor: "pointer",
          color: "var(--color-text-primary,#1a1a1a)",
        });
        retryBtn.addEventListener("click", () => _fetchContent(url, label));
        errEl.appendChild(retryBtn);
        _bodyEl.appendChild(errEl);

        if (_currentRow) {
          _currentRow.dispatchEvent(new CustomEvent("nav-row-error", {
            detail: { label, url, error: err },
            bubbles: true, composed: true,
          }));
        }
      }
    }

    function close() {
      if (!_panel) return;
      _panel.style.transform         = "translateX(100%)";
      _backdrop.style.background      = "rgba(0,0,0,0)";
      _backdrop.style.pointerEvents   = "none";
      document.body.style.overflow    = "";

      if (_currentRow) {
        _currentRow.dispatchEvent(new CustomEvent("nav-row-close", {
          detail: { label: _currentRow.getAttribute("label"), url: _currentUrl },
          bubbles: true, composed: true,
        }));
      }

      _currentUrl = null;
      _currentRow = null;
    }

    return { open, close };
  })();


  // ── Icon library ─────────────────────────────────────────────────────────
  const NAV_ROW_ICONS = {
    info: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/>
      <path d="M8 7v5M8 5.5v.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    </svg>`,
    doc: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="1.5" width="11" height="13" rx="1.5" stroke="currentColor" stroke-width="1.3"/>
      <path d="M5 5.5h6M5 8h6M5 10.5h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    </svg>`,
    shield: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5L2.5 4v4C2.5 11 5 13.5 8 14.5c3-1 5.5-3.5 5.5-6.5V4L8 1.5z"
            stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
    </svg>`,
    link: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6.5 9.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
      <path d="M9.5 6.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    </svg>`,
    star: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5l1.9 3.8 4.2.6-3 3 .7 4.2L8 11l-3.8 2.1.7-4.2-3-3 4.2-.6L8 1.5z"
            stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
    </svg>`,
    settings: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.3"/>
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.1 3.1l1.4 1.4M11.5 11.5l1.4 1.4M3.1 12.9l1.4-1.4M11.5 4.5l1.4-1.4"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
    </svg>`,
    coffee: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 6h8v5a3 3 0 01-3 3H6a3 3 0 01-3-3V6z"
            stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
      <path d="M11 7.5h1.5a1.5 1.5 0 010 3H11"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
      <path d="M5.5 3.5c0-.8.8-1 .8-2M7.5 3.5c0-.8.8-1 .8-2"
            stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    </svg>`,
    memory: `<svg fill="#ffffff" width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" d="M8.58578644,1 L19,1 C20.1045695,1 21,1.8954305 21,3 L21,21 C21,22.1045695 20.1045695,23 19,23 L5,23 C3.8954305,23 3,22.1045695 3,21 L3,6.58578644 L8.58578644,1 Z M5,7.41421356 L5,21 L19,21 L19,3 L9.41421356,3 L5,7.41421356 Z M16,4 L18,4 L18,10 L16,10 L16,4 Z M13,4 L15,4 L15,10 L13,10 L13,4 Z M10,4 L12,4 L12,10 L10,10 L10,4 Z M7,7 L9,7 L9,11 L7,11 L7,7 Z"/>
      </svg>`,
  };

  const CHEVRON_RIGHT = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.4"
          stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;


  // ── Web Component ─────────────────────────────────────────────────────────
  class NavRow extends HTMLElement {

    // ── A. No Shadow DOM — renders as a plain block so it inherits the
    //       parent card's border and background automatically ───────────────
    constructor() {
      super();
      this._connected = false;
      this._action = null; // function reference for action mode
    }

    // ── B. Lifecycle ──────────────────────────────────────────────────────
    connectedCallback() {
      this._connected = true;
      this._render();
    }

    // ── C. Observed attributes ────────────────────────────────────────────
    static get observedAttributes() {
      return ["label", "sublabel", "icon", "title", "url"];
    }

    attributeChangedCallback() {
      if (this._connected) this._render();
    }

    // ── D. Property accessors (mirror attributes) ─────────────────────────
    get label()    { return this.getAttribute("label")    || ""; }
    get sublabel() { return this.getAttribute("sublabel") || ""; }
    get icon()     { return this.getAttribute("icon")     || ""; }
    get title()    { return this.getAttribute("title")    || this.label; }
    get url()      { return this.getAttribute("url")      || ""; }

    set label(v)    { this.setAttribute("label",    v); }
    set sublabel(v) { this.setAttribute("sublabel", v); }
    set icon(v)     { this.setAttribute("icon",     v); }
    set title(v)    { this.setAttribute("title",    v); }
    set url(v)      { this.setAttribute("url",      v); }

    // ── Action property (function reference) ────────────────────────────
    // Allows setting a JavaScript function that will be called when the row
    // is pressed, instead of opening the overlay. Set programmatically:
    //   row.action = () => { ... }
    get action() { return this._action; }
    set action(fn) {
      if (typeof fn !== "function" && fn !== null) {
        throw new Error("nav-row action must be a function or null");
      }
      this._action = fn;
    }

    // ── E. Open / Execute ──────────────────────────────────────────────
    open() {
      // If an action is set, execute it instead of opening the overlay
      if (typeof this._action === "function") {
        this._action(this);
        return;
      }
      if (!this.url) return;
      NavRowOverlay.open(this, {
        label: this.label,
        title: this.title,
        url:   this.url,
      });
    }

    close() {
      NavRowOverlay.close();
    }

    // ── F. Render ─────────────────────────────────────────────────────────
    _render() {
      const iconSVG = NAV_ROW_ICONS[this.icon] || "";
      const hasSub  = !!this.sublabel;

      // Inline styles keep this component self-contained and compatible with
      // any parent card without requiring a Shadow DOM or a shared stylesheet.
      this.innerHTML = `
        <style>
          nav-row {
            display: block;
            border-top: 0.5px solid var(--color-border-tertiary, #e0dfd7);
          }
          /*nav-row:first-of-type { border-top: none; }*/
          .nr-inner {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 16px;
            gap: 12px;
            cursor: pointer;
            transition: background 0.12s;
            -webkit-tap-highlight-color: transparent;
            user-select: none;
          }
          .nr-inner:hover   { background: var(--color-background-secondary, #f5f5f3); }
          .nr-inner:active  { background: var(--color-background-secondary, #f5f5f3); }
          .nr-inner:focus-visible {
            outline: 2px solid var(--color-border-info, #4a90d9);
            outline-offset: -2px;
          }
          .nr-left {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
            flex: 1;
          }
          .nr-icon-wrap {
            width: 32px; height: 32px;
            border-radius: 8px;
            background: var(--color-background-info, #5D4037);
            color: var(--color-text-info, #ffffff);
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
          .nr-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
          .nr-label {
            font-size: 14px;
            font-weight: 500;
            color: var(--color-text-primary, #1a1a1a);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .nr-sublabel {
            font-size: 11px;
            color: var(--color-text-tertiary, #aaa);
          }
          .nr-arrow {
            color: var(--color-text-tertiary, #aaa);
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .nr-inner:hover .nr-arrow { transform: translateX(2px); }
        </style>

        <div class="nr-inner" role="button" tabindex="0"
             aria-label="${this.label}${this.sublabel ? " – " + this.sublabel : ""}">
          <div class="nr-left">
            ${iconSVG ? `<div class="nr-icon-wrap">${iconSVG}</div>` : ""}
            <div class="nr-text">
              <span class="nr-label">${this.label}</span>
              ${hasSub ? `<span class="nr-sublabel">${this.sublabel}</span>` : ""}
            </div>
          </div>
          <span class="nr-arrow">${CHEVRON_RIGHT}</span>
        </div>`;

      const inner = this.querySelector(".nr-inner");
      inner.addEventListener("click", () => this.open());
      inner.addEventListener("keydown", e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.open();
        }
      });
    }
  }

export { NavRow }